import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { BookOpen, Broadcast, MapPin, Flask } from "@phosphor-icons/react";

export default function MethodologyDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="methodology-button"
          className="flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-[#e5e7eb] text-[#1f2937] hover:bg-[#eef7ed] hover:-translate-y-[1px] transition-transform transition-colors"
        >
          <BookOpen size={15} weight="bold" />
          Metodología
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-[#ffffff] border-[#e5e7eb] text-[#1a2318] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="plex text-xl tracking-tight">
            Metodología & Sobre el proyecto
          </DialogTitle>
          <DialogDescription className="text-[#334155] text-sm">
            Fuentes de datos, modelo espacial y funciones analíticas del geoportal.
          </DialogDescription>
        </DialogHeader>

        <img
          src="https://images.unsplash.com/photo-1723745756728-a885332de118?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxkb21pbmljYW4lMjByZXB1YmxpYyUyMGxhbmRzY2FwZSUyMGNvbnN0YW56YXxlbnwwfHx8fDE3ODcwMjIyODZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Constanza, República Dominicana"
          className="w-full h-44 object-cover rounded-md border border-[#e5e7eb] my-2"
        />

        <div className="space-y-5 text-sm text-[#1f2937] leading-relaxed">
          <p className="text-[#1f2937] italic border-l-2 border-[#4a9b3c] pl-3">
            "Influencia de la variabilidad térmica sobre los cambios en las áreas agrícolas del
            municipio de Constanza durante el período 2000–2024 mediante técnicas de teledetección
            y análisis geoespacial."
          </p>
          <p>
            Geoportal desarrollado como producto de trabajo de grado para analizar la relación entre la{" "}
            <span className="text-[#3f8e33] font-medium">variabilidad térmica</span> y los{" "}
            <span className="text-[#3f8e33] font-medium">cambios en las áreas agrícolas</span>{" "}
            del municipio de <span className="font-medium">Constanza</span>, La Vega,
            República Dominicana (2000–2024).
          </p>

          <div className="flex gap-3">
            <Broadcast size={20} className="text-[#3f8e33] mt-0.5 shrink-0" />
            <div>
              <div className="font-medium text-[#1a2318]">Fuentes de teledetección</div>
              <p className="text-[#334155] text-[13px]">
                <span className="mono text-[#1f2937]">NDVI</span> y <span className="mono text-[#1f2937]">temperatura de
                superficie (LST)</span> de <span className="mono text-[#1f2937]">Landsat C2 L2</span> (30 m, vía Google
                Earth Engine): Landsat 5 TM / 7 ETM+ (2000–2012) y Landsat 8/9 OLI/TIRS (2013–2024). Cobertura del suelo
                de <span className="mono text-[#1f2937]">ESA WorldCover 10 m</span> (Sentinel-2). Temperatura del aire
                oficial del <span className="mono text-[#1f2937]">INDOMET</span> y reanálisis <span className="mono text-[#1f2937]">ERA5-Land</span>.
              </p>
              <p className="text-[#334155] text-[13px] mt-1.5">
                Los compuestos usan la <strong>temporada seca (dic–mar)</strong> con máscara de nubes, sombras y
                píxeles de relleno (QA_PIXEL). <strong>Landsat 7 ETM+ opera en modo SLC-off desde 2003</strong>, con
                franjas sin dato; dichos píxeles se enmascaran (aparecen como huecos, no como rayas) y, cuando otra
                escena o Landsat 5 cubre la zona, el compuesto de temporada seca los rellena.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <MapPin size={20} className="text-[#3f8e33] mt-0.5 shrink-0" />
            <div>
              <div className="font-medium text-[#1a2318]">Zonas agrícolas y cambios</div>
              <p className="text-[#334155] text-[13px]">
                Las zonas agrícolas se identifican con la capa de uso del suelo (ESA WorldCover 10 m, clase Cropland).
                El análisis de cambios compara el NDVI entre dos años (p. ej. 2000 vs 2024) y cuantifica las hectáreas
                con <span className="text-green-400">aumento de NDVI</span> o <span className="text-amber-400">disminución de NDVI</span> (umbral ±0.05),
                restringible a las tierras de cultivo.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Flask size={20} className="text-[#3f8e33] mt-0.5 shrink-0" />
            <div>
              <div className="font-medium text-[#1a2318]">Análisis de correlación</div>
              <p className="text-[#334155] text-[13px]">
                Coeficiente de correlación de Pearson (r) y regresión lineal entre la temperatura de
                superficie anual (Landsat) y el NDVI del valle, con exportación de datos (CSV) y
                figuras (PNG) para el trabajo de grado.
              </p>
            </div>
          </div>

          <div className="rounded-md border border-amber-900/40 bg-amber-950/20 p-3 text-[12px] text-amber-200/80">
            <strong>Nota académica:</strong> el NDVI y la LST provienen de teledetección satelital real (Landsat, 30 m).
            La correlación describe una <strong>relación observada</strong> y no implica causalidad: el NDVI también depende
            de la precipitación, el riego y el manejo agrícola. Los resultados corresponden al período de estudio 2000–2024.
          </div>

          <div className="rounded-md border border-[#e5e7eb] bg-[#f8faf9] p-3 text-[12px] text-[#334155]">
            <div className="font-medium text-[#1a2318] mb-1.5">Fuentes de datos y licencias</div>
            <ul className="space-y-1">
              <li><strong>Landsat C2 L2</strong> (LST y NDVI): cortesía del <a className="text-[#3f8e33] underline" href="https://www.usgs.gov/centers/eros/data-citation" target="_blank" rel="noreferrer">USGS/NASA</a>, dominio público, vía Google Earth Engine.</li>
              <li><strong>ESA WorldCover 10 m</strong> (uso del suelo): © ESA WorldCover project, <a className="text-[#3f8e33] underline" href="https://esa-worldcover.org" target="_blank" rel="noreferrer">CC BY 4.0</a>.</li>
              <li><strong>ERA5-Land</strong> (temp. del aire): © <a className="text-[#3f8e33] underline" href="https://www.ecmwf.int" target="_blank" rel="noreferrer">ECMWF/Copernicus</a> (licencia Copernicus).</li>
              <li><strong>INDOMET</strong> (temp. oficial): Oficina Nacional de Meteorología, Rep. Dominicana.</li>
              <li><strong>Mapas base</strong>: © <a className="text-[#3f8e33] underline" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors (ODbL); Esri World Imagery (Esri, Maxar); © <a className="text-[#3f8e33] underline" href="https://carto.com/attributions" target="_blank" rel="noreferrer">CARTO</a>.</li>
            </ul>
          </div>

          <div className="pt-2 border-t border-[#e5e7eb] text-[12px] text-[#475569]">
            © {new Date().getFullYear()} GeoConstanza · Universidad Nacional Pedro Henríquez Ureña (UNPHU) · Escuela de Geomática
            <div className="mt-1 text-[#64748b]">Producto de trabajo de grado · Autores: Argelis Génere Benítez · Luis Miguel Zacarías Peralta. Todos los derechos reservados. Los datos pertenecen a sus respectivas fuentes según sus licencias.</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
